import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { io, type Socket } from 'socket.io-client';
import { resolveApiOrigin } from '@vipso/shared';
import { tenantApi } from '../api/tenantApi';
import { applyOrgDataChanged, type OrgDataChangedPayload } from '../realtime/applyOrgDataChanged';
import { store, type RootState } from '../store';
import { setAuthFromInit, setTokens } from '../store/authSlice';
import { mapInitAppPayload } from '../store/authUtils';

/** Collapse duplicate force-refresh emits (domain + admin belt) into one quiet session patch. */
const FORCE_REFRESH_DEBOUNCE_MS = 400;

function featureKeysEqual(a: string[] | undefined, b: string[] | undefined) {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/** True when lock / features / brand are unchanged — skip full auth re-render. */
function sessionShellUnchanged(
  auth: RootState['auth'],
  next: ReturnType<typeof mapInitAppPayload>,
) {
  return (
    Boolean(auth.operationsLocked) === Boolean(next.operationsLocked) &&
    featureKeysEqual(auth.featureKeys, next.featureKeys) &&
    (auth.theme?.primaryColor ?? '') === (next.theme?.primaryColor ?? '') &&
    (auth.theme?.logoUrl ?? '') === (next.theme?.logoUrl ?? '') &&
    (auth.org?.primaryColor ?? '') ===
      ((next.org as { primaryColor?: string } | null)?.primaryColor ?? '') &&
    (auth.org?.logoUrl ?? '') === ((next.org as { logoUrl?: string } | null)?.logoUrl ?? '')
  );
}

export function useSocket(enabled = true) {
  const socketRooms = useSelector((state: RootState) => state.auth.socketRooms);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!enabled || !socketRooms.length) return;

    const fromEnv = String(import.meta.env.VITE_SOCKET_URL || '')
      .trim()
      .replace(/\/$/, '');
    const url =
      fromEnv && /^https?:\/\//i.test(fromEnv) ? fromEnv : resolveApiOrigin();
    if (!url) return;

    const socket = io(url, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
    });
    socketRef.current = socket;

    let forceRefreshTimer: ReturnType<typeof setTimeout> | null = null;
    let forceRefreshInFlight = false;

    const subscribe = () => {
      socket.emit('online');
      socket.emit('subscribe', { rooms: socketRooms });
    };

    const applyQuietSessionRefresh = async () => {
      if (forceRefreshInFlight) return;
      forceRefreshInFlight = true;
      try {
        const refreshToken = store.getState().auth.refreshToken;
        if (!refreshToken) return;
        const result = await store
          .dispatch(tenantApi.endpoints.refreshSession.initiate({ refreshToken }))
          .unwrap();
        const payload = result.refreshSession;
        const auth = store.getState().auth;
        const next = mapInitAppPayload(payload, {
          tenant: auth.tenant,
          theme: auth.theme,
          user: auth.user,
        });
        // Never resetApiState. If shell fields are unchanged, only rotate tokens
        // so list pages / Settings do not re-render from a no-op force-refresh.
        if (sessionShellUnchanged(auth, next)) {
          if (
            auth.accessToken !== next.accessToken ||
            auth.refreshToken !== next.refreshToken
          ) {
            store.dispatch(
              setTokens({
                accessToken: next.accessToken,
                refreshToken: next.refreshToken,
              }),
            );
          }
          return;
        }
        store.dispatch(setAuthFromInit(next));
      } catch {
        // Ignore — next boot/login will recover
      } finally {
        forceRefreshInFlight = false;
      }
    };

    socket.on('connect', subscribe);

    socket.on('org-data-changed', (payload: OrgDataChangedPayload) => {
      applyOrgDataChanged(payload);
    });

    socket.on('force-refresh', () => {
      if (forceRefreshTimer) clearTimeout(forceRefreshTimer);
      forceRefreshTimer = setTimeout(() => {
        forceRefreshTimer = null;
        void applyQuietSessionRefresh();
      }, FORCE_REFRESH_DEBOUNCE_MS);
    });

    return () => {
      if (forceRefreshTimer) clearTimeout(forceRefreshTimer);
      socket.off('connect', subscribe);
      socket.off('org-data-changed');
      socket.off('force-refresh');
      socket.disconnect();
      socketRef.current = null;
    };
  }, [enabled, socketRooms.join('|')]);

  return socketRef;
}
